import { useState, useEffect, useRef, useCallback } from 'react';
import { KaldiRecognizer, Model } from 'vosk-browser';
import logger from '@/utils/logger';
import { detectWakeWord, getMatchedWakeWords } from '@/utils/wakeWordDetector';
import { voskModelManager } from '@/lib/managers/VoskModelManager';

interface RecognizerResult {
  result: {
    text: string;
  };
}

interface RecognizerPartialResult {
  result: {
    partial: string;
  };
}

interface UseVoskProps {
  onWakeWordDetected: () => void;
  wakeWords: string[];
  enabled: boolean;
  enableHomophone?: boolean; // 是否启用同音字检测
}

interface UseVoskReturn {
  status: string;
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
  history: string[];
  partial: string;
}

export const useVosk = ({
  onWakeWordDetected,
  wakeWords,
  enabled,
  enableHomophone = true,
}: UseVoskProps): UseVoskReturn => {
  const [status, setStatus] = useState<string>(enabled ? 'idle' : 'disabled');
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const MAX_HISTORY_ITEMS = 50;
  const [partial, setPartial] = useState('');

  const recognizerRef = useRef<KaldiRecognizer | null>(null);
  const modelRef = useRef<Model | null>(null);
  const onWakeWordDetectedRef = useRef(onWakeWordDetected);
  const wakeWordsRef = useRef(wakeWords); // 保持最新的唤醒词引用
  const enableHomophoneRef = useRef(enableHomophone); // 保持最新的同音字配置
  const lastPartialRef = useRef(''); // 用于存储上一次的局部结果
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recognizerNodeRef = useRef<AudioWorkletNode | ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  useEffect(() => {
    onWakeWordDetectedRef.current = onWakeWordDetected;
  }, [onWakeWordDetected]);

  // 保持 wakeWords 和 enableHomophone 的最新引用
  useEffect(() => {
    wakeWordsRef.current = wakeWords;
  }, [wakeWords]);

  useEffect(() => {
    enableHomophoneRef.current = enableHomophone;
  }, [enableHomophone]);

  // 监听 enabled 状态变化
  useEffect(() => {
    if (!enabled) {
      setStatus('disabled');
    } else if (status === 'disabled') {
      // 从禁用变为启用时，设置为空闲状态（不立即加载模型）
      setStatus('idle');
    }
  }, [enabled, status]);

  // 初始化识别器（从模型创建）
  const initializeRecognizer = useCallback(
    (model: Model) => {
      try {
        // 不再传入语法，创建一个通用的识别器
        const recognizer = new model.KaldiRecognizer(16000);
        recognizerRef.current = recognizer;

        recognizer.on('result', (message: any) => {
          const resultText = (message as RecognizerResult).result.text;
          if (resultText) {
            logger.debug('[Vosk] Final Result:', resultText);
            setHistory((prev) => {
              const next = [...prev, resultText];
              if (next.length > MAX_HISTORY_ITEMS) next.splice(0, next.length - MAX_HISTORY_ITEMS);
              return next;
            });
            setPartial('');

            // 使用共享的唤醒词检测逻辑（通过 ref 获取最新值，避免闭包陷阱）
            const currentWakeWords = wakeWordsRef.current;
            const currentEnableHomophone = enableHomophoneRef.current;
            const isWakeWordDetected = detectWakeWord(resultText, currentWakeWords, currentEnableHomophone);
            if (isWakeWordDetected) {
              const matchedWords = getMatchedWakeWords(resultText, currentWakeWords, currentEnableHomophone);
              logger.info('[Vosk] 唤醒词检测成功:', {
                原文: resultText,
                匹配的唤醒词: matchedWords,
                启用同音字: currentEnableHomophone,
                当前唤醒词列表: currentWakeWords,
              });
              onWakeWordDetectedRef.current();
            }
          }
        });

        recognizer.on('partialresult', (message: any) => {
          const partialText = (message as RecognizerPartialResult).result.partial;
          // 仅当局部结果变化时才打印日志
          if (partialText && partialText !== lastPartialRef.current) {
            logger.debug('[Vosk] Partial Result:', partialText);
            lastPartialRef.current = partialText;
          }
          setPartial(partialText);
        });

        logger.info('[Vosk] 识别器初始化成功');
      } catch (e) {
        logger.error('[Vosk] 识别器初始化失败:', e);
        throw e;
      }
    },
    [wakeWords, enableHomophone]
  );

  // 清理函数
  useEffect(() => {
    return () => {
      recognizerRef.current?.remove();
      // 注意：不在这里清理模型，因为模型由 VoskModelManager 管理
      // 确保音频资源被释放
      try {
        recognizerNodeRef.current?.disconnect();
        gainNodeRef.current?.disconnect();
        sourceRef.current?.disconnect();
      } catch {}
      recognizerNodeRef.current = null;
      gainNodeRef.current = null;
      sourceRef.current = null;
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }
    };
  }, []);

  const start = useCallback(async () => {
    if (!enabled) {
      logger.warn('[Vosk] 启动失败：Vosk 未启用');
      return;
    }

    try {
      // 懒加载模型：仅在第一次 start 时才加载模型
      if (!modelRef.current) {
        logger.info('[Vosk] 首次启动，开始懒加载模型...');
        setStatus('loading_model');
        setError(null);

        try {
          const model = await voskModelManager.loadModel();
          modelRef.current = model;
          logger.info('[Vosk] 模型加载成功，初始化识别器');

          // 初始化识别器
          initializeRecognizer(model);

          setStatus('ready');
        } catch (loadError) {
          const errorMsg = `模型加载失败: ${loadError}`;
          logger.error('[Vosk]', errorMsg);
          setError(errorMsg);
          setStatus('error');
          return;
        }
      }

      // 确保识别器已初始化（在懒加载之后检查）
      if (!recognizerRef.current) {
        logger.error('[Vosk] 启动失败：识别器未初始化');
        setError('识别器初始化失败');
        setStatus('error');
        return;
      }

      // 开始音频采集
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
      mediaStreamRef.current = mediaStream;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(mediaStream);
      sourceRef.current = source;

      const TARGET_SAMPLE_RATE = 16000;

      if ((audioContext as any).audioWorklet) {
        // 使用 AudioWorkletNode 进行音频处理
        const workletCode = `
          class VoskProcessor extends AudioWorkletProcessor {
            process(inputs) {
              const input = inputs[0];
              if (input && input[0]) {
                // 将第一声道数据传回主线程
                this.port.postMessage(input[0]);
              }
              return true;
            }
          }
          registerProcessor('vosk-processor', VoskProcessor);
        `;
        const blob = new Blob([workletCode], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        await audioContext.audioWorklet.addModule(url);
        URL.revokeObjectURL(url);

        const workletNode = new AudioWorkletNode(audioContext, 'vosk-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 0,
        });
        recognizerNodeRef.current = workletNode;

        workletNode.port.onmessage = (event: MessageEvent) => {
          try {
            const inputData = event.data as Float32Array;
            if (!(inputData && inputData.length)) return;

            if (audioContext.sampleRate !== TARGET_SAMPLE_RATE) {
              const outputLength = Math.floor(
                (inputData.length * TARGET_SAMPLE_RATE) / audioContext.sampleRate
              );
              const outputBuffer = audioContext.createBuffer(1, outputLength, TARGET_SAMPLE_RATE);
              const outputData = outputBuffer.getChannelData(0);
              const ratio = audioContext.sampleRate / TARGET_SAMPLE_RATE;
              let R = 0;
              for (let i = 0; i < outputLength; i++) {
                const p1 = Math.floor(R);
                const p2 = Math.min(p1 + 1, inputData.length - 1);
                const pFrac = R - p1;
                const v1 = inputData[p1] || 0;
                const v2 = inputData[p2] || 0;
                outputData[i] = v1 + (v2 - v1) * pFrac;
                R += ratio;
              }
              recognizerRef.current?.acceptWaveform(outputBuffer);
            } else {
              const directBuffer = audioContext.createBuffer(
                1,
                inputData.length,
                TARGET_SAMPLE_RATE
              );
              directBuffer.copyToChannel(inputData, 0, 0);
              recognizerRef.current?.acceptWaveform(directBuffer);
            }
          } catch (error) {
            logger.error('acceptWaveform failed', error);
          }
        };

        // 链接：仅连接到 worklet，不连接到 destination，避免回放
        source.connect(workletNode);
      } else {
        // 旧浏览器回退：使用 ScriptProcessorNode
        const recognizerNode = audioContext.createScriptProcessor(4096, 1, 1);
        recognizerNodeRef.current = recognizerNode;

        recognizerNode.onaudioprocess = (event: AudioProcessingEvent) => {
          try {
            const inputBuffer = event.inputBuffer;
            if (audioContext.sampleRate !== TARGET_SAMPLE_RATE) {
              const inputData = inputBuffer.getChannelData(0);
              const outputLength = Math.floor(
                (inputData.length * TARGET_SAMPLE_RATE) / audioContext.sampleRate
              );
              const outputBuffer = audioContext.createBuffer(1, outputLength, TARGET_SAMPLE_RATE);
              const outputData = outputBuffer.getChannelData(0);

              const ratio = audioContext.sampleRate / TARGET_SAMPLE_RATE;
              let R = 0;
              for (let i = 0; i < outputLength; i++) {
                const p1 = Math.floor(R);
                const p2 = Math.ceil(R);
                const pFrac = R - p1;
                const v1 = inputData[p1] || 0;
                const v2 = inputData[p2] || 0;
                outputData[i] = v1 + (v2 - v1) * pFrac;
                R += ratio;
              }
              recognizerRef.current?.acceptWaveform(outputBuffer);
            } else {
              recognizerRef.current?.acceptWaveform(inputBuffer);
            }
          } catch (error) {
            logger.error('acceptWaveform failed', error);
          }
        };

        source.connect(recognizerNode);

        // 创建一个增益为0的GainNode，避免音频反馈循环，同时保持onaudioprocess的触发
        const gainNode = audioContext.createGain();
        gainNodeRef.current = gainNode;
        gainNode.gain.value = 0;
        recognizerNode.connect(gainNode);
        gainNode.connect(audioContext.destination);
      }

      setStatus('listening');
    } catch (e) {
      const errorMsg = `启动失败: ${e}`;
      logger.error('[Vosk]', errorMsg);
      setError(errorMsg);
      setStatus('error');
    }
  }, [enabled, initializeRecognizer]);

  const stop = useCallback(() => {
    try {
      recognizerNodeRef.current?.disconnect();
      gainNodeRef.current?.disconnect();
      sourceRef.current?.disconnect();
    } catch {}
    recognizerNodeRef.current = null;
    gainNodeRef.current = null;
    sourceRef.current = null;
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch {}
      audioContextRef.current = null;
    }
    setStatus((prev) => (prev === 'listening' ? 'ready' : prev));
  }, []);

  return { status, error, start, stop, history, partial };
};
